import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Input, Select, Tag, Space, Modal, Form,
  Drawer, Descriptions, InputNumber, message, Tabs, List, Typography,
  Popconfirm, Tooltip, Image, Spin,
} from 'antd';
import {
  PlusOutlined, ImportOutlined, SearchOutlined, EyeOutlined,
  EditOutlined, PlayCircleOutlined, CheckCircleOutlined, CheckOutlined,
  ReloadOutlined, HistoryOutlined, RollbackOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { orderApi, alarmApi } from '../services/api';
import { fmtCN, nowCN } from '../utils/time';
import { actionText, fieldText } from '../utils/logLabels';
import { parseContent, stripAppendDividers, logDeltaText, cleanLogRemark, renderLatestSegment } from '../utils/logRender';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { navigateToOrderDetail } from './MainLayout';
import CreateOrderModal from './CreateOrderModal';
import OrderAlarmBell from './OrderAlarmBell';

const { TextArea } = Input;
const { Text } = Typography;

// 紧急程度：3级（对应value=urgent/high/normal，兼容后端）
const priorityOptions = [
  { value: 'urgent', label: '紧急重要' },
  { value: 'high', label: '一般重要' },
  { value: 'normal', label: '普通' },
];

const priorityColor = { urgent: 'red', high: 'orange', normal: 'blue', low: 'default' };
const priorityText = { urgent: '紧急重要', high: '一般重要', normal: '普通', low: '普通' };
// 工单状态统一中文显示
const statusColorMap = { pending: 'gold', processing: 'blue', awaiting_customer: 'purple', completed: 'green' };
const statusTextMap = { pending: '调度待跟进', processing: '调度处理中', awaiting_customer: '待客服处理', completed: '已完成' };

// 提醒周期显示标签（custom = 系统按优先级策略固定）
const reminderCycleLabels = {
  hourly: '每小时', daily: '每天', every_2_days: '每2天', weekly: '每周', custom: '系统策略', none: '不提醒',
};

export default function OrderList({ status, title, desc, defaultPriority = '' }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  // priorityFilter 默认值优先级：URL ?priority=urgent → 组件 defaultPriority（如 UrgentOrders 固定 urgent）→ 空
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || defaultPriority || '');

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderLogs, setOrderLogs] = useState([]);
  // 调度思路/跟进策略 追加编辑弹窗
  const [strategyModalOpen, setStrategyModalOpen] = useState(false);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [strategyType, setStrategyType] = useState(''); // 'thought' | 'plan'
  const [strategyText, setStrategyText] = useState('');
  const [strategyAttachments, setStrategyAttachments] = useState([]);
  const [strategyUploading, setStrategyUploading] = useState(false);

  // 详情打开策略：路径 /orders/detail/:id 存在时用抽屉显示订单（保持路由前进/后退可用）
  useEffect(() => {
    const match = location.pathname.match(/\/orders\/detail\/(\d+)$/);
    if (match) {
      const id = parseInt(match[1], 10);
      if (!Number.isNaN(id)) {
        handleOpenDetail(id);
      }
    } else {
      if (detailDrawerOpen) setDetailDrawerOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // URL → priorityFilter 同步（用户通过 ?priority=urgent 进入或在页面内改 URL）
  useEffect(() => {
    const fromUrl = searchParams.get('priority') || defaultPriority || '';
    if (fromUrl !== priorityFilter) {
      setPriorityFilter(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, defaultPriority]);

  const [editForm] = Form.useForm();
  const [actionForm] = Form.useForm();
  const [backForm] = Form.useForm();
  const [resultForm] = Form.useForm();
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [backModalOpen, setBackModalOpen] = useState(false);
  // 邮件匹配后调度补填处理结果和过程 → 流转待客服
  const [resultModalOpen, setResultModalOpen] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [page, pageSize, priorityFilter, status]);

  // 当前用户已设闹钟的工单ID集合（铃铛高亮用；设置/取消后由 OrderAlarmBell 回调刷新）
  const [alarmIds, setAlarmIds] = useState(new Set());
  const fetchAlarmIds = async () => {
    try {
      const d = await alarmApi.mine('order');
      setAlarmIds(new Set(d.order_ids || []));
    } catch (e) {
      /* 拦截器已提示 */
    }
  };
  useEffect(() => {
    fetchAlarmIds();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = { status, page, page_size: pageSize };
      if (keyword) params.keyword = keyword;
      if (priorityFilter) params.priority = priorityFilter;
      const data = await orderApi.list(params);
      setOrders(data.items);
      setTotal(data.total);
    } catch (e) {} finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchOrders();
  };

  // priorityFilter 改变 → 同步到 URL query（便于刷新后仍保留筛选 + 卡片跳转进来保持一致）
  // 例外：如果当前页自带 defaultPriority（如 UrgentOrders 固定 urgent），且与 priorityFilter 一致 → URL 不额外加 query，保持 URL 干净
  useEffect(() => {
    const next = {};
    for (const [k, v] of searchParams.entries()) next[k] = v;
    const cur = next.priority || '';
    const want = (defaultPriority && priorityFilter === defaultPriority) ? '' : priorityFilter;
    if (cur === want) return;
    if (want) next.priority = want; else delete next.priority;
    const qs = new URLSearchParams(next).toString();
    setSearchParams(qs ? Object.fromEntries(new URLSearchParams(qs)) : {}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityFilter, defaultPriority]);

  const handleOpenDetail = async (orderId) => {
    try {
      const order = await orderApi.get(orderId);
      setCurrentOrder(order);
      const logs = await orderApi.getLogs(orderId);
      setOrderLogs(Array.isArray(logs) ? logs : (logs?.items || []));
      setDetailDrawerOpen(true);
    } catch (e) {
      message.error(e.message || '获取订单详情失败');
    }
  };

  const handleImport = async () => {
    if (!importText.trim()) {
      message.warning('请粘贴要导入的文本内容');
      return;
    }
    setImporting(true);
    try {
      const result = await orderApi.import({ raw_text: importText });
      if (result.success > 0) {
        message.success(`成功导入 ${result.success} 条订单`);
        setImportModalOpen(false);
        setImportText('');
        fetchOrders();
      }
      if (result.errors.length > 0) {
        result.errors.forEach(e => message.warning(e));
      }
    } catch (e) {} finally {
      setImporting(false);
    }
  };

  const openDetail = async (order) => {
    navigateToOrderDetail(navigate, order.id);
  };

  // 给 Dashboard 跳转明细按钮也能走的公开 helper
  useEffect(() => {
    window.__openOrderDetail = (id) => navigateToOrderDetail(navigate, id);
    return () => { delete window.__openOrderDetail; };
  }, [navigate]);

  const openEdit = async (order) => {
    setCurrentOrder(order);
    // 核心问题留空（历史由下方区块展示，通过「追加/修改」弹窗维护）
    const masked = { ...order, core_issue: '' };
    editForm.setFieldsValue(masked);
    setEditDrawerOpen(true);
    try {
      const logs = await orderApi.getLogs(order.id);
      setOrderLogs(Array.isArray(logs) ? logs : (logs?.items || []));
    } catch (e) {}
  };

  // URL ?edit=<id> 自动打开编辑 Drawer（从详情抽屉"去编辑"跳转过来）
  const editHandledRef = React.useRef(null);
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || editHandledRef.current === editId) return;
    editHandledRef.current = editId;
    (async () => {
      try {
        const order = await orderApi.get(parseInt(editId, 10));
        openEdit(order);
      } catch (e) {}
      // 延迟清除参数，避免 useEffect 重跑
      setTimeout(() => {
        const next = {};
        for (const [k, v] of searchParams.entries()) { if (k !== 'edit') next[k] = v; }
        setSearchParams(next, { replace: true });
      }, 300);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleEdit = async (values) => {
    if (currentOrder?.status === 'completed') {
      const newIssue = (values.new_issue || '').trim();
      if (!newIssue) { message.error('请填写新的工单需求'); return; }
      try {
        await orderApi.reopen(currentOrder.id, { new_issue: newIssue });
        message.success('已重新打开，工单流转至「调度待跟进」');
        setEditDrawerOpen(false);
        fetchOrders();
      } catch (e) {}
      return;
    }
    try {
      delete values.core_issue;
      delete values.handling_strategy;
      delete values.follow_up_plan;
      await orderApi.update(currentOrder.id, values);
      message.success('更新成功');
      setEditDrawerOpen(false);
      fetchOrders();
    } catch (e) {}
  };

  const openAction = async (order) => {
    actionForm.resetFields();
    setCurrentOrder(order);
    setActionModalOpen(true);
    // 拉取最新值，保证必填校验与详情页互通（列表行可能是旧缓存）
    try {
      const fresh = await orderApi.get(order.id);
      setCurrentOrder(fresh);
    } catch (e) {}
  };

  const handleAction = async (values) => {
    // 核心处理思路/跟进策略必填（与详情编辑页同一字段，数据互通）
    if (!(currentOrder?.handling_strategy || '').trim()) { message.error('请先填写「核心处理思路」'); return; }
    if (!(currentOrder?.follow_up_plan || '').trim()) { message.error('请先填写「跟进策略」'); return; }
    try {
      await orderApi.recordAction(currentOrder.id, values);
      message.success('操作记录已保存，订单已流转到处理中');
      setActionModalOpen(false);
      fetchOrders();
    } catch (e) {}
  };

  const handleComplete = async (order) => {
    try {
      await orderApi.complete(order.id);
      message.success('工单已结束');
      fetchOrders();
    } catch (e) {}
  };

  // 处理中 → 回退待跟进（须填写实际进展内容，需求4.2）
  const openBackToPending = (order) => {
    setCurrentOrder(order);
    backForm.resetFields();
    setBackModalOpen(true);
  };

  const handleBackToPending = async (values) => {
    try {
      await orderApi.backToPending(currentOrder.id, { progress_note: values.progress_note });
      message.success('已流转回待跟进');
      setBackModalOpen(false);
      fetchOrders();
    } catch (e) {}
  };

  // 处理中流转待客服 / 邮件匹配后补填结果：实际操作记录+结果反馈必填（与详情编辑页同一字段，数据互通）
  const openResultModal = (order) => {
    setCurrentOrder(order);
    resultForm.resetFields();
    setResultModalOpen(true);
  };

  const handleToAwaiting = async (values) => {
    try {
      await orderApi.toAwaitingCustomer(currentOrder.id, {
        result_note: values.result_note,
        actual_action: values.actual_action || '',
      });
      message.success('已流转至待客服处理工单');
      setResultModalOpen(false);
      fetchOrders();
    } catch (e) {}
  };

  // 是否已在编辑页填好「实际操作记录 + 结果反馈」（与流转弹窗同源字段）
  const hasPrefilledResult = (order) =>
    Boolean((order?.result_feedback || '').trim()) && Boolean((order?.actual_action || '').trim());

  // 编辑页已填写 → 直接以已存字段流转，无需重复弹出填写框
  const transferWithSavedResult = async (order) => {
    try {
      await orderApi.toAwaitingCustomer(order.id, {
        result_note: (order.result_feedback || '').trim(),
        actual_action: (order.actual_action || '').trim(),
      });
      message.success('已流转至待客服处理工单');
      fetchOrders();
    } catch (e) {}
  };

  // —— 调度员追加/修改「核心处理思路」或「跟进策略」 ——
  const openStrategyEditor = async (type) => {
    setStrategyType(type);
    setStrategyText('');  // 弹窗空着让用户写新内容（不复用旧值）
    setStrategyAttachments([]);
    setStrategyModalOpen(true);
  };

  // 弹窗内粘贴图片/文件
  const handleStrategyPaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (!files.length) return;
    e.preventDefault();
    setStrategyUploading(true);
    try {
      const newAtts = [];
      for (const f of files) {
        const d = await orderApi.uploadFile(f);
        newAtts.push({ url: d.url, name: d.name || f.name, isImage: f.type.startsWith('image/') });
      }
      setStrategyAttachments((prev) => [...prev, ...newAtts]);
      message.success(`已粘贴 ${files.length} 个文件`);
    } catch (err) {
      message.error(err?.response?.data?.detail || '文件上传失败');
    } finally {
      setStrategyUploading(false);
    }
  };
  const saveStrategyEdit = async () => {
    if (!strategyText.trim() && !strategyAttachments.length) { message.warning('内容不能为空'); return; }
    setStrategyLoading(true);
    try {
      const fieldMap = {
        issue:   { field: 'core_issue',          label: '核心问题/异常情况' },
        thought: { field: 'handling_strategy',   label: '核心处理思路' },
        plan:    { field: 'follow_up_plan',      label: '跟进策略' },
      };
      const { field, label } = fieldMap[strategyType] || fieldMap.thought;
      const attachMd = strategyAttachments.map((a) =>
        a.isImage ? `![${a.name}](${a.url})` : `[${a.name}](${a.url})`
      ).join('\n');
      const newChunk = [strategyText.trim(), attachMd].filter(Boolean).join('\n');
      // 取最新值 → 带分隔线追加（不覆盖历史）
      const fresh = await orderApi.get(currentOrder.id);
      const oldVal = (fresh[field] || '').trim();
      const divider = `\n\n— — — — — 【${nowCN()} 调度填写】\n`;
      const merged = oldVal ? (oldVal + divider + newChunk) : newChunk;
      const updated = await orderApi.update(currentOrder.id, { [field]: merged });
      message.success(`${label}已追加`);
      setStrategyModalOpen(false);
      setStrategyAttachments([]);
      // 三字段在 Drawer 里保持空白（避免历史文本堆积在输入框），只刷新 currentOrder + 日志列表让 history 区块更新
      setCurrentOrder(updated);
      const logs = await orderApi.getLogs(currentOrder.id);
      setOrderLogs(Array.isArray(logs) ? logs : (logs?.items || []));
      fetchOrders();
    } catch (e) {
      message.error('保存失败：' + (e.message || '未知错误'));
    } finally {
      setStrategyLoading(false);
    }
  };

  // —— 渲染某字段的历史记录 ——
  // 每条 update 日志存的是累计合并文本；本次增量 = new_value 与 old_value 的差值
  const renderFieldHistory = (field, label) => {
    if (!currentOrder) return null;
    const updates = orderLogs
      .filter(l => l.action === 'update' && l.field_changed === field)
      .sort((a, b) => a.id - b.id);
    const items = [];
    if (updates.length) {
      const first = updates[0];
      // 首次录入：first.old_value 就是原始创建时的值（清理历史脏数据 "None"）
      const firstOld = first.old_value && first.old_value !== 'None' ? first.old_value : '(空)';
      items.push({
        label: '首次录入',
        value: firstOld,
        operator_name: first.operator_name || '创建人',
        created_at: first.created_at,
      });
      updates.forEach((u, i) => {
        let delta = u.new_value || '';
        let old = u.old_value || '';
        // 清理历史脏数据
        if (old === 'None') old = '';
        if (old && delta.startsWith(old)) {
          delta = delta.slice(old.length);
          delta = delta.replace(/^\s*—+\s*【[^】]+】\s*\n?/, '').trim();
        }
        items.push({
          label: i === updates.length - 1 ? '最新内容' : `第 ${i + 1} 次追加`,
          value: delta || '(无变化)',
          operator_name: u.operator_name || '未知',
          created_at: u.created_at,
        });
      });
    } else {
      const raw = currentOrder[field];
      const currentVal = (raw && raw !== 'None' ? raw : '').trim();
      if (!currentVal) return null;
      const createLog = orderLogs.find(l => l.action === 'create');
      items.push({
        label: '首次录入',
        value: currentVal,
        operator_name: createLog?.operator_name || '创建人',
        created_at: createLog?.created_at || currentOrder.created_at,
      });
    }
    const fmtTime = (t) => fmtCN(t, 'YYYY-MM-DD HH:mm:ss');
    return (
      <div style={{ margin: '-12px 0 16px', padding: '8px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
          📋 {label} 历史（共 {items.length} 条）
        </div>
        {items.map((l, i) => (
          <div key={i} style={{ borderTop: i === 0 ? 'none' : '1px dashed #e8e8e8', padding: '6px 0', fontSize: 13 }}>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 3 }}>
              <Tag color={i === 0 ? 'blue' : (i === items.length - 1 ? 'green' : 'default')} style={{ marginRight: 4 }}>{l.label}</Tag>
              操作人：{l.operator_name} · {fmtTime(l.created_at)}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', color: '#555' }}>
              {l.value && l.value.length > 300 ? parseContent(l.value.slice(0, 300) + '…') : parseContent(l.value)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const columns = [
    {
      title: '订单号',
      dataIndex: 'order_no',
      key: 'order_no',
      width: 185,
      render: (text, record) => (
        <Space size={4}>
          <Text strong copyable>{text}</Text>
          <OrderAlarmBell orderType="order" orderId={record.id} active={alarmIds.has(record.id)} onChanged={fetchAlarmIds} />
        </Space>
      ),
    },
    {
      title: '客户',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 140,
      ellipsis: true,
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (p) => <Tag color={priorityColor[p]}>{priorityText[p]}</Tag>,
    },
    {
      title: '核心问题',
      dataIndex: 'core_issue',
      key: 'core_issue',
      ellipsis: true,
      render: (text) => {
        if (!text) return <Text type="secondary">未填写</Text>;
        return <span style={{ whiteSpace: 'pre-wrap' }}>{renderLatestSegment(text, '未填写')}</span>;
      },
    },
    {
      title: '卡司',
      dataIndex: 'carrier',
      key: 'carrier',
      width: 120,
      ellipsis: true,
    },
    {
      title: '提醒周期',
      dataIndex: 'reminder_cycle',
      key: 'reminder_cycle',
      width: 110,
      render: (c, r) => (c === 'custom'
        ? `每${r.custom_reminder_hours}小时(系统)`
        : reminderCycleLabels[c] || c),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 140,
      render: (t) => fmtCN(t, 'MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="查看详情">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)} />
          </Tooltip>
          {status !== 'pending' && status !== 'processing' && (
            <Tooltip title="编辑">
              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
            </Tooltip>
          )}
          {status === 'pending' && (
            record.need_result ? (
              <Tooltip title="邮件已匹配，补填处理结果和过程后流转给客服">
                <Button type="text" size="small" style={{ color: '#722ed1' }} icon={<PlayCircleOutlined />} onClick={() => openResultModal(record)}>补填结果转客服</Button>
              </Tooltip>
            ) : (
              <Tooltip title="填写处理思路并转入处理中">
                <Button type="text" size="small" icon={<PlayCircleOutlined />} onClick={() => openAction(record)}>开始处理</Button>
              </Tooltip>
            )
          )}
          {status === 'processing' && (
            hasPrefilledResult(record) ? (
              <Popconfirm
                title="将以编辑页已填写的「实际操作记录」「结果反馈」直接流转至待客服，是否继续？"
                okText="直接流转"
                cancelText="取消"
                onConfirm={() => transferWithSavedResult(record)}
              >
                <Button type="text" size="small" style={{ color: '#52c41a' }} icon={<CheckOutlined />}>流转待客服</Button>
              </Popconfirm>
            ) : (
              <Button type="text" size="small" style={{ color: '#52c41a' }} icon={<CheckOutlined />} onClick={() => openResultModal(record)}>流转待客服</Button>
            )
          )}
          {status === 'processing' && (
            <Tooltip title="手动回到调度待跟进（正常由邮件匹配自动触发）">
              <Button type="text" size="small" icon={<RollbackOutlined />} onClick={() => openBackToPending(record)}>回退待跟进</Button>
            </Tooltip>
          )}
          {status !== 'completed' && (
            <Popconfirm title="确认结束此工单？" onConfirm={() => handleComplete(record)}>
              <Tooltip title="结束工单">
                <Button type="text" size="small" danger icon={<CheckCircleOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
          {status === 'completed' && (
            <Tooltip title="重新打开">
              <Button type="text" size="small" icon={<ReloadOutlined />} onClick={() => openEdit(record)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const renderOrderFields = () => (
    <>
      <Form.Item name="order_no" label="订单号" rules={[{ required: true, message: '请输入订单号' }]}>
        <Input placeholder="订单号/BOL号/PRO号" />
      </Form.Item>
      <Form.Item name="customer_name" label="客户名称">
        <Input placeholder="客户名称" />
      </Form.Item>
      <Form.Item name="business_type" label="业务类型" initialValue="ltl">
        <Select options={[{ value: 'ltl', label: 'LTL零担' }, { value: 'ftl', label: 'FTL整车' }]} />
      </Form.Item>
      <Form.Item name="core_issue" label={
        <Space size={6}>
          <span>核心问题/异常情况</span>
          {currentOrder && (
            <Button type="link" size="small" onClick={() => openStrategyEditor('issue')}>
              {(currentOrder.core_issue || '').trim() ? '追加/修改' : '填写'}
            </Button>
          )}
        </Space>
      }>
        <TextArea rows={3} placeholder="描述订单的核心问题或异常情况（追加/修改走上方按钮）" />
      </Form.Item>
      {renderFieldHistory('core_issue', '核心问题/异常情况')}
      <Form.Item name="handling_strategy" label={
        <Space size={6}>
          <span>核心处理思路 <span style={{color:'#ff4d4f',fontWeight:600,fontSize:13}}>（调度填写）</span></span>
          {currentOrder && (
            <Button type="link" size="small" onClick={() => openStrategyEditor('thought')}>
              {currentOrder.handling_strategy ? '追加/修改' : '填写'}
            </Button>
          )}
        </Space>
      }>
        <Input.TextArea rows={3} readOnly={status === 'processing'} style={status === 'processing' ? { background: '#fafafa', color: '#333' } : undefined} placeholder="与客服销售沟通后确定的处理思路（调度员填写，可选）" />
      </Form.Item>
      {renderFieldHistory('handling_strategy', '核心处理思路')}
      <Form.Item name="follow_up_plan" label={
        <Space size={6}>
          <span>跟进策略 <span style={{color:'#ff4d4f',fontWeight:600,fontSize:13}}>（调度填写）</span></span>
          {currentOrder && (
            <Button type="link" size="small" onClick={() => openStrategyEditor('plan')}>
              {currentOrder.follow_up_plan ? '追加/修改' : '填写'}
            </Button>
          )}
        </Space>
      }>
        <Input.TextArea rows={2} readOnly={status === 'processing'} style={status === 'processing' ? { background: '#fafafa', color: '#333' } : undefined} placeholder="具体的跟进计划和步骤（调度员填写，可选）" />
      </Form.Item>
      {renderFieldHistory('follow_up_plan', '跟进策略')}
      <Form.Item name="carrier" label="卡司/承运商">
        <Input placeholder="卡司名称" />
      </Form.Item>
      <Form.Item name="carrier_platform" label="卡车平台">
        <Input placeholder="如: Truckstop, DAT等" />
      </Form.Item>
      <Form.Item name="pickup_location" label="提货地址">
        <Input placeholder="提货地址" />
      </Form.Item>
      <Form.Item name="delivery_location" label="送货地址">
        <Input placeholder="送货地址" />
      </Form.Item>
      <Form.Item name="estimated_delivery" label="提货时间">
        <Input placeholder="提货时间" />
      </Form.Item>
    </>
  );

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>{title}</h2>
          <p className="desc">{desc}</p>
        </div>
        {status !== 'pending' && (
          <Space>
            <Button icon={<ImportOutlined />} onClick={() => setImportModalOpen(true)}>
              一键导入
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
              新建工单
            </Button>
          </Space>
        )}
      </div>

      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="搜索订单号/客户/问题/卡司"
            prefix={<SearchOutlined />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder="按优先级筛选"
            value={priorityFilter || undefined}
            onChange={(v) => { setPriorityFilter(v); setPage(1); }}
            style={{ width: 140 }}
            allowClear
            options={priorityOptions}
          />
          <Button type="primary" onClick={handleSearch}>搜索</Button>
        </Space>
      </Card>

      <Card className="card-shadow">
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 新建工单弹窗（与科维斯订单「生成调度」共用同一组件，表单/逻辑完全一致） */}
      <CreateOrderModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={fetchOrders}
      />

      {/* 一键导入弹窗 */}
      <Modal
        title="一键导入订单"
        open={importModalOpen}
        onCancel={() => setImportModalOpen(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setImportModalOpen(false)}>取消</Button>,
          <Button key="import" type="primary" loading={importing} onClick={handleImport}>
            智能识别并导入
          </Button>,
        ]}
      >
        <p style={{ color: '#666', marginBottom: 12 }}>
          粘贴微信聊天记录、邮件内容或订单信息文本，系统将自动识别订单号、客户、问题等字段并创建工单。
        </p>
        <TextArea
          rows={12}
          placeholder="粘贴文本内容..."
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
        />
      </Modal>

      {/* 订单详情抽屉（水平居中） */}
      <Drawer
        title="工单详情"
        className="order-detail-drawer"
        open={detailDrawerOpen}
        styles={{ wrapper: { marginRight: 'calc((100vw - min(1100px, 96vw))/2)' } }}
        extra={
          currentOrder ? (
            <Button type="primary" onClick={() => {
              setDetailDrawerOpen(false);
              const statusToPath = { pending: '/orders/pending', processing: '/orders/processing', awaiting_customer: '/orders/all?status=awaiting_customer', urgent: '/orders/urgent', completed: '/orders/completed' };
              const target = (currentOrder && statusToPath[currentOrder.status]) || '/orders/pending';
              navigate(`${target}${target.includes('?') ? '&' : '?'}edit=${currentOrder.id}`, { replace: true });
            }}>去编辑</Button>
          ) : null
        }
        onClose={() => {
          setDetailDrawerOpen(false);
          // 若当前路径在 detail 里，关闭抽屉 → 返回当前订单列表首页（保持 URL 与 UI 同步）
          const m = location.pathname.match(/^(\/orders\/(?:pending|processing|completed|urgent|all))\/detail\/\d+$/);
          if (m) {
            const qs = searchParams.toString();
            navigate(m[1] + (qs ? `?${qs}` : ''), { replace: true });
          } else if (location.pathname.startsWith('/orders/detail/')) {
            // Dashboard 跳 detail：关闭抽屉 → 回工作台
            navigate('/dashboard', { replace: true });
          }
        }}
        width="min(1100px, 96vw)"
      >
        {currentOrder && (
          <Tabs
            items={[
              {
                key: 'info',
                label: '订单信息',
                children: (
                  <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                    <Descriptions.Item label="订单号">{currentOrder.order_no}</Descriptions.Item>
                    <Descriptions.Item label="工单状态"><Tag color={statusColorMap[currentOrder.status]}>{statusTextMap[currentOrder.status] || '调度待跟进'}</Tag></Descriptions.Item>
                    <Descriptions.Item label="客户">{currentOrder.customer_name || '-'}</Descriptions.Item>
                    <Descriptions.Item label="优先级"><Tag color={priorityColor[currentOrder.priority]}>{priorityText[currentOrder.priority]}</Tag></Descriptions.Item>
                    <Descriptions.Item label="业务类型">{currentOrder.business_type.toUpperCase()}</Descriptions.Item>
                    <Descriptions.Item label="卡司">{currentOrder.carrier || '-'}</Descriptions.Item>
                    <Descriptions.Item label="卡车平台">{currentOrder.carrier_platform || '-'}</Descriptions.Item>
                    <Descriptions.Item label="提货地址">{currentOrder.pickup_location || '-'}</Descriptions.Item>
                    <Descriptions.Item label="送货地址">{currentOrder.delivery_location || '-'}</Descriptions.Item>
                    <Descriptions.Item label="提醒周期">
                      {currentOrder.reminder_cycle === 'custom'
                        ? `每${currentOrder.custom_reminder_hours}小时（系统按优先级固定）`
                        : reminderCycleLabels[currentOrder.reminder_cycle] || currentOrder.reminder_cycle}
                    </Descriptions.Item>
                    <Descriptions.Item label="创建时间">{fmtCN(currentOrder.created_at)}</Descriptions.Item>
                    <Descriptions.Item label="更新时间">{fmtCN(currentOrder.updated_at)}</Descriptions.Item>
                    <Descriptions.Item label="核心问题" span={2} labelStyle={{ verticalAlign: 'top', paddingTop: 12 }} contentStyle={{ minHeight: 56, verticalAlign: 'top' }}><span style={{ whiteSpace: 'pre-wrap' }}>{renderLatestSegment(currentOrder.core_issue)}</span></Descriptions.Item>
                    {currentOrder.issue_images?.length > 0 && (
                      <Descriptions.Item label="问题截图" span={2}>
                        <Image.PreviewGroup>
                          <Space wrap>
                            {currentOrder.issue_images.map((u) => (
                              <Image key={u} src={u} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} />
                            ))}
                          </Space>
                        </Image.PreviewGroup>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="处理思路" span={2} labelStyle={{ verticalAlign: 'top', paddingTop: 12 }} contentStyle={{ minHeight: 56, verticalAlign: 'top' }}><span style={{ whiteSpace: 'pre-wrap' }}>{parseContent(currentOrder.handling_strategy) || '-'}</span></Descriptions.Item>
                    <Descriptions.Item label="跟进策略" span={2} labelStyle={{ verticalAlign: 'top', paddingTop: 12 }} contentStyle={{ minHeight: 56, verticalAlign: 'top' }}><span style={{ whiteSpace: 'pre-wrap' }}>{parseContent(currentOrder.follow_up_plan) || '-'}</span></Descriptions.Item>
                    <Descriptions.Item label="实际操作" span={2}><span style={{ whiteSpace: 'pre-wrap' }}>{currentOrder.actual_action || '-'}</span></Descriptions.Item>
                    <Descriptions.Item label="结果反馈" span={2}><span style={{ whiteSpace: 'pre-wrap' }}>{currentOrder.result_feedback || '-'}</span></Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                key: 'logs',
                label: (
                  <span>
                    <HistoryOutlined /> 操作日志 ({orderLogs.length})
                  </span>
                ),
                children: (
                  <List
                    dataSource={orderLogs}
                    renderItem={(log) => (
                      <List.Item>
                        <List.Item.Meta
                          title={<Space><Tag>{actionText(log.action)}</Tag>{log.field_changed && <Text type="secondary">{fieldText(log.field_changed)}</Text>}</Space>}
                          description={
                            <div>
                              {log.remark && <div style={{ whiteSpace: 'pre-wrap' }}>{parseContent(cleanLogRemark(log.remark))}</div>}
                              {(() => {
                                const delta = logDeltaText(log);
                                if (!delta) return null;
                                if (log.action === 'status_change') {
                                  return <Text type="secondary" style={{ fontSize: 12 }}>→ {statusTextMap[log.new_value] || delta}</Text>;
                                }
                                return <div style={{ fontSize: 12, color: '#666', whiteSpace: 'pre-wrap', marginTop: 2 }}>{parseContent(delta)}</div>;
                              })()}
                              <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                                <Text strong style={{ color: log.is_system ? '#999' : '#1677ff', fontSize: 11 }}>
                                  操作人：{log.operator_name || '未知'}
                                </Text>
                                {'　'}{fmtCN(log.created_at, 'YYYY-MM-DD HH:mm:ss')}
                              </div>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                ),
              },
            ]}
          />
        )}
      </Drawer>

      {/* 编辑抽屉 */}
      <Drawer
        title="编辑工单"
        className="order-edit-drawer"
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        width={760}
        styles={{ wrapper: { marginRight: 'clamp(0px, calc((100vw - 760px)/2), 100vh)' } }}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          {renderOrderFields()}
          {currentOrder?.status === 'completed' ? (
            <Form.Item
              name="new_issue"
              label="新的工单需求"
              rules={[{ required: true, message: '请填写新的工单需求后再重新打开' }]}
            >
              <TextArea rows={6} placeholder="描述本次需要调度重新跟进的新需求 / 新异常 / 新问题" />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="result_feedback"
                label="结果反馈"
                rules={status === 'processing' ? [{ required: true, message: '请填写结果反馈' }] : []}
              >
                <TextArea rows={3} placeholder="处理结果反馈" />
              </Form.Item>
              <Form.Item
                name="actual_action"
                label="实际操作记录"
                rules={status === 'processing' ? [{ required: true, message: '请填写实际操作记录' }] : []}
              >
                <TextArea rows={2} placeholder="晚上实际操作记录" />
              </Form.Item>
            </>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {currentOrder?.status === 'completed' ? '重新打开并提交' : '保存'}
              </Button>
              <Button onClick={() => setEditDrawerOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      {/* 记录操作弹窗 */}
      <Modal
        title="开始处理"
        open={actionModalOpen}
        onCancel={() => setActionModalOpen(false)}
        width={720}
        footer={null}
      >
        <p style={{ color: '#666', marginBottom: 16 }}>
          记录实际操作并填写处理思路/跟进策略后，订单将自动流转到「调度处理中工单」。
        </p>
        <Form form={actionForm} layout="vertical" onFinish={handleAction}>
          <Form.Item name="actual_action" label="实际操作内容" rules={[{ required: true, message: '请填写实际操作内容' }]}>
            <TextArea rows={5} placeholder="记录今晚与卡司/平台的沟通内容、采取的措施、当前进展等" />
          </Form.Item>
          <Form.Item name="result_feedback" label="结果反馈（可选）">
            <TextArea rows={3} placeholder="操作后的结果和反馈" />
          </Form.Item>

          {/* 核心处理思路 / 跟进策略 快速追加（与详情编辑页同一字段，数据互通） */}
          <div style={{ border: '1px dashed #d9d9d9', borderRadius: 6, padding: '10px 14px', marginBottom: 12, background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text strong style={{ fontSize: 13 }}>
                核心处理思路
                <span style={{ color: '#ff4d4f', fontWeight: 600, marginLeft: 6, fontSize: 12 }}>（调度填写）</span>
              </Text>
              <Button type="link" size="small" onClick={() => openStrategyEditor('thought')}>
                {currentOrder?.handling_strategy ? '追加/修改' : '填写'}
              </Button>
            </div>
            <div style={{ fontSize: 12, color: currentOrder?.handling_strategy ? '#333' : '#bbb', whiteSpace: 'pre-wrap', minHeight: 20 }}>
              {parseContent(stripAppendDividers(currentOrder?.handling_strategy)) || '（暂未填写，必填）'}
            </div>
          </div>

          <div style={{ border: '1px dashed #d9d9d9', borderRadius: 6, padding: '10px 14px', marginBottom: 12, background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text strong style={{ fontSize: 13 }}>
                跟进策略
                <span style={{ color: '#ff4d4f', fontWeight: 600, marginLeft: 6, fontSize: 12 }}>（调度填写）</span>
              </Text>
              <Button type="link" size="small" onClick={() => openStrategyEditor('plan')}>
                {currentOrder?.follow_up_plan ? '追加/修改' : '填写'}
              </Button>
            </div>
            <div style={{ fontSize: 12, color: currentOrder?.follow_up_plan ? '#333' : '#bbb', whiteSpace: 'pre-wrap', minHeight: 20 }}>
              {parseContent(stripAppendDividers(currentOrder?.follow_up_plan)) || '（暂未填写，必填）'}
            </div>
          </div>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">保存并流转</Button>
              <Button onClick={() => setActionModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="流转回待跟进"
        open={backModalOpen}
        onCancel={() => setBackModalOpen(false)}
        width={560}
        footer={null}
      >
        <p style={{ color: '#666', marginBottom: 16 }}>
          根据反馈填写实际进展内容后，订单将从「处理中」流转回「待跟进」。
        </p>
        <Form form={backForm} layout="vertical" onFinish={handleBackToPending}>
          <Form.Item
            name="progress_note"
            label="实际进展内容"
            rules={[{ required: true, message: '请填写实际进展内容后再流转到待跟进' }]}
          >
            <TextArea rows={5} placeholder="如：卡司反馈明天提柜、已联系收货人约定派送时间等" />
          </Form.Item>

          {/* 核心处理思路 / 跟进策略 快速追加 */}
          <div style={{ border: '1px dashed #d9d9d9', borderRadius: 6, padding: '10px 14px', marginBottom: 12, background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text strong style={{ fontSize: 13 }}>
                核心处理思路
                <span style={{ color: '#ff4d4f', fontWeight: 600, marginLeft: 6, fontSize: 12 }}>（调度填写）</span>
              </Text>
              <Button type="link" size="small" onClick={() => openStrategyEditor('thought')}>
                {currentOrder?.handling_strategy ? '追加/修改' : '填写'}
              </Button>
            </div>
            <div style={{ fontSize: 12, color: currentOrder?.handling_strategy ? '#333' : '#bbb', whiteSpace: 'pre-wrap', minHeight: 20 }}>
              {parseContent(stripAppendDividers(currentOrder?.handling_strategy)) || '（暂未填写）'}
            </div>
          </div>

          <div style={{ border: '1px dashed #d9d9d9', borderRadius: 6, padding: '10px 14px', marginBottom: 12, background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text strong style={{ fontSize: 13 }}>
                跟进策略
                <span style={{ color: '#ff4d4f', fontWeight: 600, marginLeft: 6, fontSize: 12 }}>（调度填写）</span>
              </Text>
              <Button type="link" size="small" onClick={() => openStrategyEditor('plan')}>
                {currentOrder?.follow_up_plan ? '追加/修改' : '填写'}
              </Button>
            </div>
            <div style={{ fontSize: 12, color: currentOrder?.follow_up_plan ? '#333' : '#bbb', whiteSpace: 'pre-wrap', minHeight: 20 }}>
              {parseContent(stripAppendDividers(currentOrder?.follow_up_plan)) || '（暂未填写）'}
            </div>
          </div>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">保存并流转到待跟进</Button>
              <Button onClick={() => setBackModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 流转待客服：实际操作记录 + 结果反馈（必填，与详情编辑页同一字段） */}
      <Modal
        title="流转至待客服处理"
        open={resultModalOpen}
        onCancel={() => setResultModalOpen(false)}
        width={720}
        footer={null}
      >
        <p style={{ color: '#666', marginBottom: 16 }}>
          请填写本轮「实际操作记录」和「结果反馈」，提交后工单流转至「待客服处理工单」，由客服/销售查看并回复跟进意见。
        </p>
        <Form form={resultForm} layout="vertical" onFinish={handleToAwaiting}>
          <Form.Item
            name="actual_action"
            label="实际操作记录"
            rules={[{ required: true, message: '请填写实际操作记录后再流转给客服' }]}
          >
            <TextArea rows={4} placeholder="如：已联系卡司核实异常、已发送邮件催复、已预约送仓等" />
          </Form.Item>
          <Form.Item
            name="result_note"
            label="结果反馈"
            rules={[{ required: true, message: '请填写结果反馈后再流转给客服' }]}
          >
            <TextArea rows={4} placeholder="如：卡司已确认预约时间、异常件处理进展、最终签收/赔付结果等" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">提交并流转给客服</Button>
              <Button onClick={() => setResultModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 核心处理思路 / 跟进策略 追加编辑弹窗 */}
      <Modal
        title={strategyType === 'issue' ? '追加/修改「核心问题/异常情况」' : (strategyType === 'thought' ? '追加/修改「核心处理思路」' : '追加/修改「跟进策略」')}
        open={strategyModalOpen}
        onCancel={() => setStrategyModalOpen(false)}
        width={680}
        confirmLoading={strategyLoading}
        okText="保存"
        cancelText="取消"
        onOk={saveStrategyEdit}
      >
        <p style={{ color: '#666', fontSize: 12, marginBottom: 10 }}>
          可在原内容基础上追加新的思路/步骤，或直接改写。历史内容以操作日志形式保留追溯。
        </p>
        <TextArea
          rows={7}
          value={strategyText}
          onChange={(e) => setStrategyText(e.target.value)}
          onPaste={handleStrategyPaste}
          placeholder={strategyType === 'thought'
            ? '填写或追加本次调度的核心处理思路'
            : '填写或追加本次调度的跟进策略（具体计划/步骤），可直接 Ctrl/⌘+V 粘贴图片或文件'}
        />
        <div style={{ marginTop: 4, color: '#8c8c8c', fontSize: 12 }}>
          直接 Ctrl/⌘+V 粘贴图片或文件{strategyUploading && <Spin size="small" style={{ marginInlineStart: 8 }} />}
        </div>
        {strategyAttachments.length > 0 && (
          <Image.PreviewGroup>
            <Space wrap size={8} style={{ marginTop: 8 }}>
              {strategyAttachments.map((a) => (
                <div key={a.url} style={{ position: 'relative', width: 72, height: 72 }}>
                  {a.isImage ? (
                    <Image src={a.url} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' }} />
                  ) : (
                    <a href={a.url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 72, background: '#f5f5f5', borderRadius: 4, fontSize: 12, color: '#1890ff', textDecoration: 'none', wordBreak: 'break-all', padding: 4, textAlign: 'center' }}>
                      {a.name}
                    </a>
                  )}
                  <Button
                    size="small"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    title="删除"
                    onClick={() => setStrategyAttachments((prev) => prev.filter((x) => x.url !== a.url))}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                  />
                </div>
              ))}
            </Space>
          </Image.PreviewGroup>
        )}
      </Modal>
    </div>
  );
}
